import { useMemo } from 'react';
import { useApp } from '../contexts/AppContext';
import { computeScore, beltForScore } from '../utils/score';

/**
 * The user's score and belt, computed from the same inputs as the mobile app.
 *
 * Everything here mirrors taskdojo-app/src/contexts/ScoreContext.js:
 *
 *  - only tasks the user created or was assigned count. The web, like mobile,
 *    loads every task the user can see -- including ones they merely watch --
 *    and counting those means someone else finishing their task earns you
 *    points;
 *  - startedTasks comes from the permanent startedAt stamp, not the live
 *    status, so finishing a task keeps the point rather than moving off
 *    "in progress" and losing it;
 *  - the completions map is passed straight through, since the web already
 *    stores it keyed by local date.
 *
 * Diverging from any of that puts a user's belt at a different value depending
 * on which device they open, which is worse than having no belt at all.
 */
export function useScore() {
  const { state } = useApp();
  const uid = state.user?.uid;

  return useMemo(() => {
    const completionsByDay: Record<string, string[]> = {};
    for (const [day, c] of Object.entries(state.completions || {})) {
      completionsByDay[day] = (c as any)?.habits || [];
    }

    const mine = (t: any) =>
      t && (t.createdBy === uid || t.assignedTo === uid);

    const completedTasks = (state.tasks || []).filter(
      (t: any) => mine(t) && (t.completed || t.status === 'completed')
    ).length;

    const startedTasks = (state.tasks || []).filter(
      (t: any) => mine(t) && !!t.startedAt
    ).length;

    const result = computeScore({
      completionsByDay,
      habits: (state.habits || []) as any,
      completedTasks,
      startedTasks,
    });

    return { ...result, ...beltForScore(result.score) };
  }, [state.completions, state.habits, state.tasks, uid]);
}
